import React, { type FC, type PropsWithChildren } from 'react';

import { withImage } from '@lucid/hooks';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Text, { type TextProps } from '../text/text';
import Container from '../container/container';
import Heading, { type HeadingProps } from '../heading/heading';
import CtaWithSSO, { type SSOService } from '../cta/cta-with-sso/cta-with-sso';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import VisibleVideo from '../video/visible-video/visible-video';

export interface OverflowHeroProps extends Omit<Lucid.Directus.Generated.Hero_Section, 'id'> {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Forwards props to the Hero Heading */
  headingProps?: HeadingProps;
  cta?: Lucid.Directus.Generated.Links;
  media: Lucid.Directus.Generated.Directus_Files & {
    type: NonNullable<Lucid.Directus.Generated.Directus_Files['type']>;
  };
  /** Determines the highest breakpoint at which the items will stack */
  stackAt?: Lucid.Theme.Component.Breakpoints.VariantKeys;
  /** Determines the percentage of the container that will be occupied by each section on desktop
   *  * @default
   * [45, 65]
   * @param text - [0] - the text/cta section (left)
   * @param media - [1] - the image/video section (right)
   */
  sectionWeights?: [text: number, media: number];
  /** Forwards props to the Hero Text */
  textProps?: TextProps;
  /** For AB Test - allows to select which SSO services to use */
  ssoServices?: SSOService[];
}

const OverflowHero: FC<PropsWithChildren<OverflowHeroProps>> = ({
  className,
  heading,
  headingProps,
  text,
  cta,
  media,
  stackAt = 'bp500',
  textProps,
  sectionWeights = [45, 65],
  ssoServices = ['Google', 'Slack', 'Microsoft'],
}) => {
  const mediaType = media?.type?.split('/')[0];
  const [leftRatio, rightRatio] = sectionWeights.map(num => (num / 100) * 100);

  return (
    <Box
      css={theme => ({
        label: 'overflowHero',
        overflow: 'hidden',
        padding: `${theme.space.s64}px 0 ${theme.space.s40}px`,
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <Container
        css={theme => ({
          display: 'grid',
          gridRowGap: theme.space.s40,
          gridColumnGap: theme.space.s64,
          alignItems: 'center',
          gridTemplateColumns: '100%',
          gridTemplateRows: 'repeat(2, auto)',
          [theme.breakpoints[stackAt].min]: {
            gridTemplateRows: '1fr',
            gridTemplateColumns: `max(420px, ${leftRatio}%) ${rightRatio}%`,
          },
        })}
      >
        <Box
          css={theme => [
            {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: theme.space.s32,
              [theme.breakpoints[stackAt].min]: {
                textAlign: 'initial',
              },
            },
            media?.filename_disk && {
              [theme.breakpoints[stackAt].min]: {
                alignItems: 'start',
              },
            },
          ]}
        >
          <Heading
            {...headingProps}
            as="h1"
            css={{ margin: 0 }}
            // eslint-disable-next-line @stylistic/jsx-sort-props
            className="overflowHeroHeading"
            variant="hero2XL700"
          >
            {heading}
          </Heading>
          <Text
            {...textProps}
            css={{ margin: 0 }}
            // eslint-disable-next-line @stylistic/jsx-sort-props
            className="overflowHeroText"
            id="heroLinkDescription"
            variant="paragraphM400"
          >
            {text}
          </Text>
          <CtaWithSSO
            css={theme => ({
              p: { color: theme.colors.coolGrey[100] },
              ['p::before, p::after']: {
                borderBottom: `1px solid ${theme.colors.coolGrey[50]}`,
              },
            })}
            linkText={cta?.default_link_text}
            linkUrl={cta?.final_url}
            ssoServices={ssoServices}
          />
        </Box>
        {media?.filename_disk && (
          <Box
            css={{
              width: '120vw',
              maxWidth: 'max(620px, 100%)',
              alignSelf: 'flex-start',
            }}
          >
            {mediaType === 'video' ? (
              <VisibleVideo shouldLoop src={withImage(media)} />
            ) : (
              <ImageWithPlaceholder
                alt={media?.translations?.[0]?.alt || ''}
                aspectRatio={media.width ? (`${media.width} : ${media.height}` as Lucid.Generic.AspectRatio) : '4:3'}
                css={{ width: '100%' }}
                src={withImage(media)}
              />
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export { OverflowHero };
export default OverflowHero;
